/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ILabelProps, ILabelStyles, Label } from '@fluentui/react/lib/Label';
import { Stack } from '@fluentui/react/lib/Stack';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { LimitedAccessToFeaturesMessageBar } from 'src/core/components/LimitedAccessToFeaturesMessageBar';
import { Page } from 'src/core/components/Page';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { globalMessages } from 'src/core/utils/globalMessages';
import { AppVersionSettingsCard } from 'src/luis/appShell/managePane/appSettingsPage/AppVersionSettingsCard';
import { AppInfoCard } from 'src/modules/applicationSettings/AppInfoCard';

const messages = defineMessages({
    pageTitle: {
        id: 'luis.conversationApplicationSettingsPage.title',
        defaultMessage: 'Application Settings'
    },
    renameAppToastStart: {
        id: 'luis.conversationApplicationSettingsPage.renameAppToastStart',
        defaultMessage: 'Updating app ...'
    },
    renameAppToastSuccess: {
        id: 'luis.conversationApplicationSettingsPage.renameAppToastSuccess',
        defaultMessage: 'Update app completed'
    },
    renameAppToastFailed: {
        id: 'luis.conversationApplicationSettingsPage.renameAppToastFailed',
        defaultMessage: 'Update app failed'
    },
    updateSettingsToastStart: {
        id: 'luis.conversationApplicationSettingsPage.updateSettingsToastStart',
        defaultMessage: 'Updating settings ...'
    },
    updateSettingsToastSuccess: {
        id: 'luis.conversationApplicationSettingsPage.updateSettingsToastSuccess',
        defaultMessage: 'Update settings completed'
    },
    updateSettingsToastFailed: {
        id: 'luis.conversationApplicationSettingsPage.updateSettingsToastFailed',
        defaultMessage: 'Update settings failed'
    },
    makeEndpointPublicLabel: {
        id: 'luis.conversationApplicationSettingsPage.makeEndpointPublicLabel',
        defaultMessage: 'Make endpoints public'
    },
    endpointCalloutMessage: {
        id: 'luis.conversationApplicationSettingsPage.endpointCalloutMessage',
        defaultMessage: `For a public app, runtime access is available to everyone that has their own Azure Cognitive Service or LUIS
runtime resource, and has the public app's ID.`
    },
    on: {
        id: 'luis.conversationApplicationSettingsPage.on',
        defaultMessage: 'On'
    },
    off: {
        id: 'luis.conversationApplicationSettingsPage.off',
        defaultMessage: 'Off'
    }
});

const flexLabelStyles = classNamesFunction<ILabelProps, ILabelStyles>()({ root: { display: 'flex' } });

/**
 * Renders a page that displays the general application settings.
 * These include app specific settings like id, culture, name, etc,
 * and version specific settings like advanced training flags.
 */
export const LuisAppSettingsPage = observer(() => {
    const { localize } = useLocalization();
    const { dispatcher, rootStore } = useGlobalState();

    const currentApp = rootStore.currentLuisAppStore;
    const currentAppSettings = currentApp.appSettings;
    const isEndpointPublic = currentAppSettings?.isPublic ?? false;
    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite, canChangeAppSettingsAndDetails } = mapUserRoleToUserActions(userRole);
    React.useEffect(() => {
        dispatcher.dispatch('loadAppSettings');
        dispatcher.dispatch('getAppVersionSettings');
    }, []);

    /**
     * Updates the app  settings.
     */
    const handleUpdateAppSettings = (isPublic: boolean) => {
        dispatcher.dispatch('updateAppSettings', {
            isPublic,
            toastData: {
                running: { title: localize(messages.updateSettingsToastStart) },
                completed: { title: localize(messages.updateSettingsToastSuccess) },
                failed: { title: localize(messages.updateSettingsToastFailed) }
            }
        });
    };

    return (
        <Page pageName="LuisAppSettingsPage" size="container-fluid" title={localize(messages.pageTitle)}>
            <EditableTitle title={localize(messages.pageTitle)} />

            <Stack tokens={{ childrenGap: 20 }}>
                <AppInfoCard />

                {(!canWrite || !canChangeAppSettingsAndDetails) && (
                    <LimitedAccessToFeaturesMessageBar
                        displayedUserRole={displayedUserRole}
                        message={localize(globalMessages.cannotChangeSomeAppSettings, { displayedUserRole })}
                    />
                )}

                <Stack verticalAlign="baseline">
                    <Label styles={flexLabelStyles}>
                        {localize(messages.makeEndpointPublicLabel)}
                        <CalloutButton content={localize(messages.endpointCalloutMessage)} />
                    </Label>
                    <Toggle
                        data-automation-id="conversationSettingsIsPublic"
                        checked={isEndpointPublic}
                        onChange={(_, checked: boolean) => handleUpdateAppSettings(checked)}
                        onText={localize(messages.on)}
                        offText={localize(messages.off)}
                        disabled={!canChangeAppSettingsAndDetails}
                    />
                </Stack>

                <Stack styles={{ root: { paddingTop: 20 } }}>
                    <AppVersionSettingsCard />
                </Stack>
            </Stack>
        </Page>
    );
});
